// helpers.js - Node 22 Version (Fixed to match Node 14 behavior)

import axios from "../../../utils/axiosConfig";

// Helper function to convert canvas content to a Blob
export const canvasToBlob = (canvas) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Canvas is empty"));
        }
      },
      "image/jpeg"
    );
  });
};

// Helper function to retrieve the primary image from storage
export const getPrimaryImageFromStorage = () => {
  return window.localStorage.getItem("primaryimage");
};

// Helper function to call the face verification API
export const getFaceData = async (currentImageBlob, primaryImage) => {
  try {
    // Convert primary image to base64
    const primaryImageBase64 = await new Promise((resolve, reject) => {
      if (typeof primaryImage === "string" && primaryImage.startsWith("data:image")) {
        resolve(primaryImage.split(",")[1]);
      } else if (primaryImage instanceof Blob) {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(primaryImage);
      } else {
        reject(new Error("Invalid primary image format"));
      }
    });

    // Convert current image to base64
    const currentImageBase64 = await new Promise((resolve, reject) => {
      if (currentImageBlob instanceof Blob) {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(currentImageBlob);
      } else if (typeof currentImageBlob === "string" && currentImageBlob.startsWith("data:image")) {
        resolve(currentImageBlob.split(",")[1]);
      } else {
        reject(new Error("Invalid current image format"));
      }
    });

    // Proxied through v2-user-backend (not called directly) so the
    // face-analysis microservice credential stays server-side.
    const response = await axios.post(
      `/test/proctoring/face-match`,
      {
        primaryImage: primaryImageBase64,
        currentImage: currentImageBase64,
      },
      // Runs under "Processing Your Video" after Stop: never wait longer than this.
      { timeout: 20000 }
    );

    const { success, face_match, similarity_score, current_image_analysis } = response.data;

    // Map the API response to the expected format (matching Node 14 structure)
    return {
      success: success,
      facevisible: current_image_analysis.face_detected, // Changed from face_visible to facevisible
      numfaces: current_image_analysis.face_count, // Changed from num_faces to numfaces
      facematchresult: { // Changed from face_match_result to facematchresult
        verified: face_match,
        similarityscore: similarity_score * 100, // Changed from similarity_score to similarityscore
      },
    };
  } catch (error) {
    console.error("Error calling face verification API:", error);
    // The video service answers "no face" and "multiple faces" as 400s with a body: those are real
    // results. Anything else (timeout, network, 5xx, an unexpected body) means the photo wasn't
    // checked - unchecked: true, and the answer is uploaded instead of refused as "face not
    // detected" (candidates had to re-record because the server was slow, Nov 2026).
    const status = error.response && error.response.status;
    const data = error.response && error.response.data;
    const errorMessage = (data && data.error_message) || "";
    if (status && status < 500 && errorMessage.startsWith("No face detected in current image")) {
      return { success: false, facevisible: false, numfaces: 0, facematchresult: null, error: errorMessage };
    }
    if (status && status < 500 && errorMessage.startsWith("Multiple faces detected in current image")) {
      const faceCount = (data.current_image_analysis && data.current_image_analysis.face_count) || 2;
      return { success: true, facevisible: true, numfaces: faceCount >= 2 ? faceCount : 2, facematchresult: null, error: errorMessage };
    }
    return {
      success: false,
      unchecked: true,
      facevisible: false,
      numfaces: 0,
      facematchresult: null,
      error: errorMessage || error.message,
    };
  }
};

// Function to format recording time from seconds to "00.00 min"
export const formatRecordingTime = (timeInSeconds) => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = timeInSeconds % 60;
  return `${minutes < 10 ? "0" + minutes : minutes}.${seconds < 10 ? "0" + seconds : seconds} min`;
};

// Function to parse time string to seconds
export const parseTimeString = (time) => {
  if (typeof time === "number") {
    return time; // Already in seconds
  }
  if (typeof time === "string") {
    const [minutes, seconds] = time.replace(" min", "").split(".").map(Number);
    return minutes * 60 + seconds;
  }
  console.error("Unexpected time format:", time);
  return 0;
};

// ✅ SIMPLIFIED checkAudioQuality - Matches Node 14 behavior exactly
export const checkAudioQuality = (videoBlob, onNoAudio) => {
  return new Promise(async (resolve, reject) => {
    let audioContext;

    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const fileReader = new FileReader();

      fileReader.onload = async () => {
        try {
          const arrayBuffer = fileReader.result;
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          const channelData = audioBuffer.getChannelData(0);

          // Check for audio presence using RMS (same as Node 14)
          const rms = calculateRMS(channelData);
          console.log("Audio RMS value:", rms);

          if (rms < 0.01) {
            console.log("No audio detected (RMS below threshold)");
            onNoAudio();
            resolve("no_audio");
            return;
          }

          // ✅ If audio is present, return good_audio (Node 14 behavior)
          console.log("Audio detected, returning good_audio");
          resolve("good_audio");
        } catch (error) {
          console.error("Audio analysis error:", error);
          reject(error);
        } finally {
          if (audioContext) {
            audioContext.close();
          }
        }
      };

      fileReader.onerror = (error) => {
        console.error("FileReader error:", error);
        if (audioContext) {
          audioContext.close();
        }
        reject(error);
      };

      fileReader.readAsArrayBuffer(videoBlob);
    } catch (error) {
      console.error("Audio quality check initialization error:", error);
      if (audioContext) {
        audioContext.close();
      }
      reject(error);
    }
  });
};

// Loudness below this (RMS, -1..1 samples) is silence or background hiss, not a voice.
export const NO_AUDIO_RMS = 0.01;

// Measures a video answer's sound for the "No Audio Detected" check.
// checkAudioQuality above averages the whole recording, so the pauses in a long answer pull a
// quiet but real voice under the line. This judges the loudest tenth of the recording instead
// (half-second windows): a candidate who spoke at all passes, a silent microphone does not.
// Resolves { heard, rms, loudRms, durationSec }; rejects when the audio can't be decoded.
export const measureAudio = async (videoBlob) => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const audioContext = new AudioCtx();
  try {
    const audioBuffer = await audioContext.decodeAudioData(await videoBlob.arrayBuffer());
    const data = audioBuffer.getChannelData(0);
    const windowSize = Math.max(1, Math.round(audioBuffer.sampleRate / 2));
    const windows = [];
    for (let start = 0; start < data.length; start += windowSize) {
      windows.push(calculateRMS(data.subarray(start, Math.min(start + windowSize, data.length))));
    }
    windows.sort((a, b) => b - a);
    const loudest = windows.slice(0, Math.max(1, Math.ceil(windows.length / 10)));
    const loudRms = loudest.reduce((sum, value) => sum + value, 0) / loudest.length;
    const round = (value) => Math.round(value * 100000) / 100000;
    return {
      heard: loudRms >= NO_AUDIO_RMS,
      rms: round(calculateRMS(data)),
      loudRms: round(loudRms),
      durationSec: Math.round(audioBuffer.duration),
    };
  } finally {
    audioContext.close().catch(() => {});
  }
};

// Helper function to calculate RMS (Root Mean Square)
function calculateRMS(data) {
  if (!data || data.length === 0) {
    return 0;
  }

  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    sum += data[i] * data[i];
  }
  return Math.sqrt(sum / data.length);
}
